import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { Sport } from '@sharpr/types';
import type { MatchupBatter, MatchupResponse } from '@/hooks/useMatchupData';
import { lineupKey } from '@/hooks/useSlatePlayers';
import { RESEARCH_QUERY_OPTIONS } from './researchQueryOptions';

async function fetchMatchup(team: string, sport: Sport, lineup: string[]): Promise<MatchupResponse> {
  const params = new URLSearchParams({ team, sport });
  if (lineup.length) params.set('lineup', lineup.join(','));
  const r = await fetch(`/api/players/research/matchup?${params}`);
  if (!r.ok) throw new Error(`matchup ${r.status}`);
  return r.json();
}

export interface TeamLineupRequest {
  team: string;
  /** YYYY-MM-DD (ET) — required so a team playing on consecutive days gets distinct buckets. */
  gameDate: string;
  lineup: string[];
}

/**
 * Batched matchup fetcher for the slate directory. Shares the same React Query
 * cache key `['matchup-data', team, 'mlb', lineup.join(','), undefined]` that
 * `useMatchupData` uses, so opening the research overlay on a player rendered
 * here is a cache hit instead of a new fetch. Results are keyed by `lineupKey(team, gameDate)`.
 */
export function useSlateMatchups(requests: TeamLineupRequest[]): {
  byTeam: Map<string, MatchupBatter[]>;
  isLoading: boolean;
} {
  const results = useQueries({
    queries: requests.map(({ team, lineup }) => ({
      queryKey: ['matchup-data', team, 'mlb' as Sport, lineup.join(','), undefined] as const,
      queryFn: () => fetchMatchup(team, 'mlb', lineup),
      enabled: !!team && lineup.length > 0,
      ...RESEARCH_QUERY_OPTIONS,
    })),
  });

  const byTeam = useMemo(() => {
    const m = new Map<string, MatchupBatter[]>();
    for (let i = 0; i < requests.length; i++) {
      const data = results[i]?.data;
      if (data?.batters) m.set(lineupKey(requests[i].team, requests[i].gameDate), data.batters);
    }
    return m;
  }, [requests, results]);

  const isLoading = results.some((r) => r.isLoading);
  return { byTeam, isLoading };
}
